import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../config/supabase';
import { StockCategoryFilter } from '../types/stockCategories';

interface SubmitPlayParams {
  category: StockCategoryFilter;
  gameDate: string;
  guesses: number;
  timeMs: number;
  userId?: string | null;
  deviceId?: string | null;
  won?: boolean;
}

interface SubmitPlayResult {
  success: boolean;
  rank?: number;
  error?: string;
}

interface UseSubmitPlayOptions {
  enabled?: boolean;  // Set to false to skip submission (for dev/test mode)
}

export const useSubmitPlay = (options: UseSubmitPlayOptions = {}) => {
  const { enabled = true } = options;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const submitPlay = useCallback(async ({
    category,
    gameDate,
    guesses,
    timeMs,
    userId = null,
    deviceId = null,
    won = true
  }: SubmitPlayParams): Promise<SubmitPlayResult> => {
    // Skip submission in dev mode
    if (!enabled) {
      console.log('[DEV] useSubmitPlay disabled - skipping submission');
      return { success: true, rank: 0 };
    }

    setIsSubmitting(true);

    try {
      const clampedTime = Math.max(0, Math.min(timeMs, 1_800_000));

      const { data, error } = await supabase.rpc('upsert_game_play', {
        p_user_id: userId,
        p_device_id: deviceId,
        p_category: category,
        p_game_date: gameDate,
        p_guesses: guesses,
        p_time_ms: clampedTime,
        p_won: won
      });

      if (error) {
        throw error;
      }

      // Bust any cached leaderboards for this category/date/user combo so both modals stay in sync
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key)
            && key[0] === 'leaderboard'
            && key[1] === category
            && key[2] === gameDate;
        }
      });

      return { success: true, rank: data?.rank };
    } catch (err) {
      console.error('Failed to submit play:', err);
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to submit'
      };
    } finally {
      setIsSubmitting(false);
    }
  }, [enabled, queryClient]);

  return { submitPlay, isSubmitting };
};
